import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { getOrderNumber } from '../../utils/orderTitle';
import { getHireUrgencyBand } from '../../utils/hireUrgency';
import { hireOverdueSignalLabel } from '../../utils/hireOverdueSignal';
import { BTN_XS } from '../../constants/buttons';
import { HIRE_OPEN_HIGHLIGHT, openHireProps } from './hireHighlight';
import { isHireOrderCancelled } from '../../utils/hireBuckets';
import { STATUS_PILL_WEIGHT } from '../ui/StatusPill';
import { daysDiff } from '../../utils/ganttDates';

/*
 * 🎨 Extend = the house GREEN (`BTN_XS.confirm`), Off Hired = NEUTRAL
 * (`BTN_XS.secondary`) — owner ruling 2026-09-27, after "Extend is blue and
 * Off Hired is green": *"Green is the ultimate save action available if
 * multiple options exist"* (2026-09-25). Was hand-rolled soft tints (blue-50 /
 * green-50). The same pair is used by the Gantt tooltip and the hires Table
 * view (`HireTableView.js`) so the three surfaces cannot disagree. Pinned by
 * `HireCard.actionColours.test.js`.
 */

/**
 * "Bring Back On Hire" — the card's soft blue, unchanged by the 2026-09-27
 * ruling (it is the ONLY action on an off-hired card, so no green/neutral
 * choice arises). (The Table rendered it too until its actions column was
 * removed, 2026-09-28.)
 */
export const HIRE_REHIRE_BTN =
  'inline-flex items-center justify-center text-xs px-2 py-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200 rounded font-medium transition-colors';

// The amber-triangle path from `HireActionModal.js:167`, recoloured for this
// signal (`text-red-600 dark:text-red-400`) — the same path used on
// `HireDashboardTable.js`; never re-drawn a third time.
export const OVERDUE_TRIANGLE_PATH =
  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z';

/**
 * Calculate days remaining or overdue, plus (for `on_hire` only)
 * `daysUntilReturn` — the raw day count `getHireUrgencyBand` needs to
 * resolve the unified red/amber band. `pending` (days-until-START) and
 * `off_hired` are deliberately NOT fed into the band — see the note at the
 * band's call site below.
 */
export function getDaysInfo(item) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (item.hire_status === 'pending') {
    const start = new Date(item.hire_from + 'T00:00:00');
    // 🕰️ `daysDiff` ROUNDS — a ceil over two local midnights adds a day
    // across a clock change (`hireDays.clockChange.test.js`). All three
    // counts below use it.
    const days = daysDiff(today, start);
    return { label: `Starts in ${days} day${days !== 1 ? 's' : ''}`, urgent: days <= 1 };
  }

  if (item.hire_status === 'on_hire') {
    const end = new Date(item.hire_to + 'T00:00:00');
    const days = daysDiff(today, end);
    // 🪤 `days` is NaN when `item.hire_to` is null (unknown return date) —
    // `Number.isFinite` inside `getHireUrgencyBand` catches that and answers
    // null (render nothing), never red. Do not `!hire_to`-guard here.
    const label = days <= 0 ? 'Ends today' : `${days} day${days !== 1 ? 's' : ''} left`;
    return { label, daysUntilReturn: days };
  }

  if (item.hire_status === 'overdue') {
    const end = new Date(item.hire_to + 'T00:00:00');
    const days = daysDiff(end, today);
    return { label: `${days} day${days !== 1 ? 's' : ''} overdue` };
  }

  if (item.hire_status === 'off_hired') {
    return { label: `Returned ${formatDate(item.actual_return_date)}` };
  }

  return { label: '' };
}

/**
 * Get rate display string.
 *
 * 🔴 THE `total` TYPE SAYS "each", NOT "total" (2026-08-20) — AND THE STORED
 * VALUE IS UNCHANGED; only the word is. All three rate columns are PER-ITEM
 * rates that `hireCostForDays` multiplies by `quantity` (product-owner ruling,
 * see `hireLineQuantity` in `utils/hireCost.js`), so on a quantity-3 line this
 * cell read "£1,050 total" beside an Expected Cost of £3,150 — the word
 * asserting it WAS the line's cost while the figure beside it disagreed.
 * `total` is the schema DEFAULT for `hire_rate_type`, so this is the ORDINARY
 * line, not an edge case.
 *
 * 🪤 "/day" and "/week" carry the same per-item ambiguity and are deliberately
 * LEFT ALONE. The distinction is that a unit rate READS as a unit rate — nobody
 * mistakes "£50/day" for a line total — whereas "total" states the wrong claim
 * outright. Making the Rate column per-line throughout is a product decision
 * about what a buyer expects to read, not a bug fix; it is on the backlog.
 */
export function getRateDisplay(item, fmt) {
  const type = item.hire_rate_type || 'total';
  switch (type) {
    case 'daily':
      return `${fmt(item.daily_rate)}/day`;
    case 'weekly':
      return `${fmt(item.weekly_rate)}/week`;
    case 'total':
    default:
      return `${fmt(item.total_hire_rate)} each`;
  }
}

/**
 * Hire card component for the Kanban board.
 * Shows item details, urgency indicator, costs, and action buttons.
 */
// `openItemId` (optional, 2026-09-28): the hire the Table has open — this card
// wears `hireHighlight.js`'s mark when it is that hire. Absent → unchanged.
function HireCard({ item, canManage, onExtend, onOffHire, onReHire, onViewOrder, onCancelOrder, onOpenHire, openItemId = null }) {
  const { formatMoney } = useVatMode();
  const daysInfo = getDaysInfo(item);
  const overdueLabel = hireOverdueSignalLabel(item, formatMoney);
  // 🔀 2026-08-18 — resolves through the SHARED band (`utils/hireUrgency.js`),
  // the same one the sidebar badge (`routes/hires.js#computeUrgency`) reads,
  // rather than this card's own private amber-<=3/overdue-only-red rule.
  // Owner ruling: "I think red should mean late or today. amber is close."
  // 🚨 RED now fires on `daysUntilReturn <= 0` while STILL `on_hire`, not
  // only on the `overdue` STATUS — a hire returning today used to render
  // amber here (`isEndingSoon`); it is red now. `daysUntilReturn` is only
  // meaningful for `on_hire` (`pending`/`off_hired` pass `undefined`, which
  // `getHireUrgencyBand` treats as "unknown" — no band, exactly as before).
  const urgencyBand = getHireUrgencyBand(
    item.hire_status === 'on_hire' ? daysInfo.daysUntilReturn : undefined,
    item.hire_status
  );
  // 🔴 LEFT ALONE, NOT THIS CHANGE'S JOB: the `pending` (days-until-START)
  // "starts soon" amber text is `daysInfo.urgent`, untouched — it answers a
  // different question (time to PREPARE, not time to RETURN) and was never
  // part of the four drifted return-urgency definitions this unifies.
  const pendingUrgent = item.hire_status === 'pending' && daysInfo.urgent;
  const isOpen = openItemId != null && item.item_id === openItemId;
  // 🛡️ DEFENCE IN DEPTH (2026-09-28 review, fix round 1): a hire whose order is
  // cancelled/rejected offers no Extend / Off hire / Bring back, WHATEVER the
  // caller passes — `utils/hireBuckets.js`'s `isHireOrderCancelled`, the same
  // predicate the Table's Cancelled tab and `HireOrderExpansion.js`'s hire
  // actions gate on. The Kanban itself already excludes these rows (they land
  // in `bucketHires`' `cancelled` bucket, which it never reads), so this card
  // should never be asked to render one — but a caller that forgets to filter
  // (as `AdminHires.js` did until this round) must not offer the buttons.
  const orderCancelled = isHireOrderCancelled(item);

  // Border styling based on urgency
  let borderClass = 'border-gray-200';
  if (urgencyBand === 'red') borderClass = 'border-red-400 animate-pulse-border';
  else if (urgencyBand === 'amber') borderClass = 'border-amber-400';

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border-2 ${borderClass} dark:border-gray-600 shadow-sm p-3 mb-3 hover:shadow-md transition-shadow cursor-pointer${isOpen ? ` ${HIRE_OPEN_HIGHLIGHT}` : ''}`}
      data-testid={`hire-card-${item.item_id}`}
      {...openHireProps(item.item_id, openItemId)}
      title={isOpen ? 'The hire you have open in the Table' : undefined}
      // A click on the HIRE opens the hire (the hires Table, expanded) when the
      // page offers it (`onOpenHire`, `hooks/useHiresViewSwitch.js`); otherwise
      // (AdminHires) its order, as before.
      onClick={() => (onOpenHire ? onOpenHire(item) : onViewOrder && onViewOrder(item.order_id))}
    >
      {/* Header: Description + Order ref */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" title={item.description}>
            {item.description || 'Unnamed item'}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {getOrderNumber(item)} • {item.supplier_name || 'No supplier'}
          </p>
        </div>
        {/* Status badge */}
        <StatusBadge status={item.hire_status} />
      </div>

      {/* Hire dates */}
      <div className="text-xs text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1">
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{formatDate(item.hire_from)} → {formatDate(item.hire_to)}</span>
        {item.hire_extended_count > 0 && (
          <span className="ml-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200 rounded text-[10px] font-medium">
            ext ×{item.hire_extended_count}
          </span>
        )}
      </div>

      {/* Rate + Days info */}
      <div className="flex items-center justify-between text-xs mb-2">
        <span className="text-gray-500 dark:text-gray-400">{getRateDisplay(item, formatMoney)}</span>
        <span className={`font-medium ${urgencyBand === 'red' ? 'text-red-600 dark:text-red-400' : (urgencyBand === 'amber' || pendingUrgent) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'}`}>
          {daysInfo.label}
        </span>
      </div>

      {/* Cost row */}
      <div className="flex items-center justify-between text-xs border-t border-gray-100 dark:border-gray-700 pt-2 mb-2">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Expected: </span>
          <span className="font-medium text-gray-700 dark:text-gray-200">{formatMoney(item.expected_cost)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Current: </span>
          <span
            className={`inline-flex items-center gap-1 font-medium ${
              overdueLabel || item.current_cost > item.expected_cost
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-700 dark:text-gray-200'
            }`}
            title={overdueLabel || undefined}
          >
            {overdueLabel && (
              <svg
                className="w-4 h-4 inline text-red-600 dark:text-red-400"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={OVERDUE_TRIANGLE_PATH} />
              </svg>
            )}
            {formatMoney(item.current_cost)}
          </span>
          {overdueLabel && <span className="sr-only">{overdueLabel}</span>}
        </div>
      </div>

      {/* Requester */}
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        <span className="font-medium dark:text-gray-300">{item.requested_by}</span>
        {item.production_name && <span> • {item.production_name}</span>}
      </div>

      {/* Action buttons - only shown when management actions are available */}
      {canManage && !orderCancelled && item.hire_status !== 'off_hired' && (
        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={(e) => { e.stopPropagation(); onExtend(item); }}
            className={`${BTN_XS.confirm} flex-1 min-h-[44px] md:min-h-0 justify-center`}
            data-testid={`extend-btn-${item.item_id}`}
          >
            Extend
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onOffHire(item); }}
            className={`${BTN_XS.secondary} flex-1 min-h-[44px] md:min-h-0 justify-center`}
            data-testid={`offhire-btn-${item.item_id}`}
          >
            Off Hired
          </button>
          {item.hire_status === 'pending' && onCancelOrder && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancelOrder(item.order_id); }}
              className="flex-1 min-h-[44px] md:min-h-0 inline-flex items-center justify-center text-xs px-2 py-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-300 rounded font-medium transition-colors"
              data-testid={`cancel-btn-${item.item_id}`}
              title="Go to order to cancel this hire"
            >
              Cancel
            </button>
          )}
        </div>
      )}
      {/* Re-hire button for off-hired items */}
      {canManage && !orderCancelled && item.hire_status === 'off_hired' && onReHire && (
        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={(e) => { e.stopPropagation(); onReHire(item); }}
            className={`${HIRE_REHIRE_BTN} flex-1 min-h-[44px] md:min-h-0`}
            data-testid={`rehire-btn-${item.item_id}`}
          >
            Bring Back On Hire
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-amber-100 text-amber-700',
    on_hire: 'bg-blue-100 text-blue-700',
    overdue: 'bg-red-100 text-red-700',
    off_hired: 'bg-gray-100 text-gray-600',
  };
  const labels = {
    pending: 'Upcoming',
    on_hire: 'On Hire',
    overdue: 'Overdue',
    off_hired: 'Off-Hired',
  };

  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_PILL_WEIGHT} whitespace-nowrap ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
}

export default HireCard;
